import React, { useState, useEffect } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { ToastNotification } from 'carbon-components-react';
import { PartialHeaderLeftSideNoPanelLayout } from 'layouts/partialHeader.leftSideNoPanel.layout';
import { useSaga } from 'use-reducer-saga';
import { useComponentMount } from 'utilities/hooks.util';
import { APP_URL } from 'types';
import { LocalDispatchContext, LocalStoreContext } from './localStore/context';
import reducer, { initialState } from './localStore/reducer';
import sagas from './localStore/sagas';
import { getOrganisationGroupsStart } from './localStore/actions';
import OrganisationsList from './components/organisationsList';
import OrganisationsDetail from './components/organisationDetail';
import NewOrganisationForm from './components/newOrganisationForm';
import TransactionLimitsForm from './components/transactionLimitsForm';

export const SysAdminOrg: React.FC = () => {
  const history = useHistory();
  const location = useLocation<{ remain: boolean }>();
  const [localStore, localDispatch] = useSaga(reducer, initialState, sagas);
  const [showNewForm, setShowNewForm] = useState<boolean>(false);
  const [selectedOrgId, setSelectedOrgId] = useState<number>(0);
  const [refreshTransactionLimitsCounter, setRefreshTransactionLimitsCounter] =
    useState<number>(0);
  const [showTransactionForm, setShowTransactionForm] =
    useState<boolean>(false);
  const [editData, setEditData] = useState<any>(null);
  const [showNewOrgResponseModal, setShowNewOrgResponseModal] =
    useState<boolean>(false);
  const [toastNotificationData, setToastNotificationData] = useState<any>(null);
  const { orgId } = useParams<{ orgId?: string }>();

  const handleBacktoList = () => {
    setSelectedOrgId(0);
    history.replace(APP_URL.SYSADMIN_ORG, { remain: true });
  };

  const handleToastNotificationClose: Function = () => {
    setToastNotificationData(null);
    setShowNewOrgResponseModal(false);
  };

  useEffect(() => {
    const selected = Number(orgId);
    if (Number.isInteger(selected)) {
      setSelectedOrgId(selected);
    } else {
      if (history.location.pathname !== APP_URL.SYSADMIN_ORG) {
        history.replace(APP_URL.SYSADMIN_ORG);
      }
    }
  }, [orgId, history]);

  useEffect(() => {
    if (location.pathname === APP_URL.SYSADMIN_ORG && !location.state?.remain) {
      setSelectedOrgId(0);
    }
  }, [location]);

  useComponentMount(() => {
    localDispatch(getOrganisationGroupsStart());
  });

  return (
    <LocalStoreContext.Provider value={localStore}>
      <LocalDispatchContext.Provider value={localDispatch}>
        <div
          id="SysAdminOrg"
          data-testid="SysAdminOrg"
          className="sys-admin-org"
        >
          {showNewOrgResponseModal && toastNotificationData && (
            <ToastNotification
              kind={toastNotificationData.kind}
              lowContrast
              caption=""
              iconDescription=""
              subtitle={toastNotificationData.subtitle}
              timeout={toastNotificationData.kind === 'error' ? 5000 : 3000}
              className={
                toastNotificationData.kind === 'error' ? 'toast-error' : 'toast'
              }
              title={
                toastNotificationData.kind === 'error' ? 'Error occurred' : ''
              }
              onCloseButtonClick={() => handleToastNotificationClose()}
              onClose={() => handleToastNotificationClose()}
            />
          )}
          <PartialHeaderLeftSideNoPanelLayout
            enableHeaderGlobalBar={false}
            showLeftHandSideNav={true}
          >
            {selectedOrgId !== 0 && (
              <OrganisationsDetail
                selectedId={selectedOrgId}
                onBack={handleBacktoList}
                onOpenTransactionLimitsForm={() => setShowTransactionForm(true)}
                onRefreshTransactionLimits={() =>
                  setRefreshTransactionLimitsCounter(
                    refreshTransactionLimitsCounter + 1
                  )
                }
                refreshTransactionLimitsCounter={
                  refreshTransactionLimitsCounter
                }
                onOpenNewForm={() => setShowNewForm(true)}
                setEditData={setEditData}
              />
            )}
            {showNewForm && (
              <NewOrganisationForm
                onClose={() => setShowNewForm(false)}
                onOpenTransactionLimitsForm={() => setShowTransactionForm(true)}
                setToastNotificationData={setToastNotificationData}
                setShowNewOrgResponseModal={setShowNewOrgResponseModal}
                setEditData={setEditData}
                editData={editData}
              />
            )}
            {showTransactionForm && selectedOrgId !== 0 && (
              <TransactionLimitsForm
                organisationId={selectedOrgId}
                onRefreshTransactionLimits={() =>
                  setRefreshTransactionLimitsCounter(
                    refreshTransactionLimitsCounter + 1
                  )
                }
                onClose={() => setShowTransactionForm(false)}
                showOverlay={!showNewForm}
              />
            )}
            <OrganisationsList
              onOpenNewForm={() => setShowNewForm(true)}
              hide={selectedOrgId !== 0}
            />
          </PartialHeaderLeftSideNoPanelLayout>
        </div>
      </LocalDispatchContext.Provider>
    </LocalStoreContext.Provider>
  );
};

export default React.memo(SysAdminOrg);
